/**
 * SMS Check-in Service
 * 
 * Handles SMS-based patient check-ins for walk-in patients without HealthDesk accounts.
 * Creates minimal visit records with just phone number and queue position.
 */

import { db } from './firebase';
import firebase from 'firebase/compat/app';
import { normalizePhoneNumber } from '@healthdesk/shared-utils';

// ============================================================================
// SMS CHECK-IN MANAGEMENT
// ============================================================================

/**
 * Create an SMS check-in visit (minimal data - phone only)
 * @param {string} clinicId - Clinic ID
 * @param {string} phone - Patient phone number
 * @param {string} createdBy - User ID who created the check-in (staff)
 * @returns {Promise<Object>} Visit record { visitId, patientNumber, clinicId }
 */
export async function createSMSCheckIn(clinicId, phone, createdBy) {
  if (!clinicId || !phone) {
    throw new Error('Missing required parameters: clinicId and phone');
  }

  // Get next patient number for today
  const patientNumber = await getNextPatientNumber(clinicId);

  const visitData = {
    clinicId: clinicId,
    phone: normalizePhoneNumber(phone),
    patientNumber: patientNumber,
    checkInMethod: 'sms',
    status: 'checked_in',
    checkedInAt: firebase.firestore.FieldValue.serverTimestamp(),
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    createdBy: createdBy || null,
  };

  const visitRef = await db
    .collection('clinics')
    .doc(clinicId)
    .collection('visits')
    .add(visitData);


  return {
    visitId: visitRef.id,
    patientNumber: patientNumber,
    clinicId: clinicId,
    checkedInAt: new Date(),
  };
}

/**
 * Get next patient number for today's SMS check-ins
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<number>} Next patient number
 */
export async function getNextPatientNumber(clinicId) {
  if (!clinicId) return 1;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  try {
    const snapshot = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('visits')
      .where('checkInMethod', '==', 'sms')
      .where('checkedInAt', '>=', today)
      .get();

    // Find highest patient number
    let maxNumber = 0;
    snapshot.docs.forEach(doc => {
      const num = doc.data().patientNumber || 0;
      if (num > maxNumber) maxNumber = num;
    });

    return maxNumber + 1;
  } catch (error) {
    console.error('[SMSCheckInService] Error getting next patient number:', error);
    return 1;
  }
}

/**
 * Process incoming SMS check-in request
 * Called by SMS webhook/function when patient texts to check in
 * @param {string} clinicId - Clinic ID (determined from SMS number)
 * @param {string} fromPhone - Patient's phone number
 * @param {string} messageBody - SMS message content
 * @returns {Promise<Object>} Result with response message
 */
export async function processIncomingSMSCheckIn(clinicId, fromPhone, messageBody) {
  if (!clinicId || !fromPhone) {
    throw new Error('Missing required parameters');
  }

  try {
    // Check if patient already checked in today
    const existingCheckIn = await getTodaySMSCheckIn(clinicId, fromPhone);
    
    if (existingCheckIn) {
      return {
        success: true,
        alreadyCheckedIn: true,
        patientNumber: existingCheckIn.patientNumber,
        responseMessage: `You are already checked in as Patient #${existingCheckIn.patientNumber}. We will notify you when it's your turn.`,
      };
    }

    // Create new check-in
    const result = await createSMSCheckIn(clinicId, fromPhone, null);

    return {
      success: true,
      alreadyCheckedIn: false,
      patientNumber: result.patientNumber,
      visitId: result.visitId,
      responseMessage: `You are now checked in as Patient #${result.patientNumber}. Please wait and we will text you when it's your turn.`,
    };
  } catch (error) {
    console.error('[SMSCheckInService] Error processing SMS check-in:', error);
    return {
      success: false,
      error: error.message,
      responseMessage: 'Sorry, we could not process your check-in. Please try again or speak with staff.',
    };
  }
}

/**
 * Get today's SMS check-in for a phone number
 * @param {string} clinicId - Clinic ID
 * @param {string} phone - Patient phone number
 * @returns {Promise<Object|null>} Existing check-in or null
 */
export async function getTodaySMSCheckIn(clinicId, phone) {
  if (!clinicId || !phone) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const normalizedPhone = normalizePhoneNumber(phone);

  try {
    const snapshot = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('visits')
      .where('checkInMethod', '==', 'sms')
      .where('phone', '==', normalizedPhone)
      .where('checkedInAt', '>=', today)
      .limit(1)
      .get();

    if (snapshot.empty) return null;

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      checkedInAt: doc.data().checkedInAt?.toDate?.() || doc.data().checkedInAt,
    };
  } catch (error) {
    console.error('[SMSCheckInService] Error checking existing check-in:', error);
    return null;
  }
}

/**
 * Send notification SMS to patient
 * @param {string} clinicId - Clinic ID
 * @param {string} visitId - Visit ID
 * @param {string} message - Message to send
 * @returns {Promise<void>}
 */
export async function sendPatientNotification(clinicId, visitId, message) {
  if (!clinicId || !visitId || !message) {
    throw new Error('Missing required parameters');
  }

  // Get visit to get phone number
  const visitDoc = await db
    .collection('clinics')
    .doc(clinicId)
    .collection('visits')
    .doc(visitId)
    .get();

  if (!visitDoc.exists) {
    throw new Error('Visit not found');
  }

  const visit = visitDoc.data();
  const phone = visit.phone || visit.cmrSnapshot?.['patient.phone'];

  if (!phone) {
    throw new Error('No phone number found for this visit');
  }

  // Queue message for sending (Firebase function will handle actual SMS)
  await db.collection('messages').add({
    to: phone,
    body: message,
    clinicId: clinicId,
    visitId: visitId,
    type: 'patient_notification',
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    status: 'pending',
  });

  // Update visit to mark as notified
  await db
    .collection('clinics')
    .doc(clinicId)
    .collection('visits')
    .doc(visitId)
    .update({
      notifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @param {boolean} masked - Whether to mask middle digits
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(phone, masked = false) {
  if (!phone) return '';
  
  const normalized = normalizePhoneNumber(phone);
  
  if (masked) {
    // Show first 2 and last 4 digits only
    return normalized.replace(/(\+?\d{2})\d{6}(\d{4})/, '$1******$2');
  }
  
  // Format as +1 (555) 123-4567
  const match = normalized.match(/^\+?1?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
  }
  
  return phone;
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  createSMSCheckIn,
  getNextPatientNumber,
  processIncomingSMSCheckIn,
  getTodaySMSCheckIn,
  sendPatientNotification,
  formatPhoneNumber,
};
